// What Ejar holds for the customer's PARKING, on the same basis as our figures (VAT included).
//
// Every contract opened at the airport carries an automatic AED 50 "Parking Fees" line (the airport
// car-park fee, owner's rule): it is charged to the customer but it is NOT his portal parking, so it must
// never be compared with the Salik parking rows. Any other parking is ALWAYS an Other Charge - Ejar has no
// other place for it ("Dubai Mall parking" 19.05 + VAT = 20.00); Salik is typed sometimes as the Tolls line and
// sometimes as an Other Charge (owner, 27 Sep 2026). So what Ejar holds = the SUM of both places (a split entry
// counts whole; a trip typed twice shows as "Ejar has X more") - the same rule as the engine's reconcile.
import { VAT_MULTIPLIER } from '../../constants/finance';
import { EJAR_CALC_ITEM } from '../../constants/ejar';

export const AIRPORT_PARKING_FEE = 50;
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const isAirportBranch = (branch) => /airport|terminal/i.test(String(branch || ''));

const calcItems = (ej) => ej?.calculation?.calculationResult?.calculationItems || [];

/** { airport, parkingLine, feeLine, otherLabelled, customer } - every figure with VAT. */
export function ejarParking(ej, branch) {
    const item = calcItems(ej).find((x) => Number(x.itemTypeId) === EJAR_CALC_ITEM.parkingFees) || null;
    const line = item ? r2(item.netPrice ?? item.totalPrice) : 0;          // netPrice = with VAT
    const airport = isAirportBranch(branch);
    const fee = airport ? AIRPORT_PARKING_FEE * VAT_MULTIPLIER : 0;
    const fromLine = r2(Math.max(0, line - fee));
    // the engine marks each line by its own rule (isParking - a staff "Marina Walk-DXB 29.07.2026" names a portal
    // place and has no parking word: owner, 13 Oct 2026); an unmarked line reads by its word
    const labelled = (ej?.otherCharges || []).filter((x) => !x?.isDeleted && (typeof x?.isParking === 'boolean'
        ? x.isParking : /park|mawaqif|مواقف|باركينج/i.test(String(x?.comment || ''))));
    const otherLabelled = r2(labelled.reduce((a, x) => a + (Number(x.amount?.value) || 0) * (x.isTaxable ? VAT_MULTIPLIER : 1), 0));
    return { airport, parkingLine: line, feeLine: r2(Math.min(line, fee)), otherLabelled, customer: r2(fromLine + otherLabelled) };
}

/** The airport fee line as Ejar holds it: AED 50 before VAT is the expected figure. */
export const airportFeeOk = (item) => Math.abs((Number(item?.totalPrice) || 0) - AIRPORT_PARKING_FEE) < 0.01;

/**
 * Salik + Darb as Ejar holds them, with VAT: the Tolls line (netPrice) PLUS the Salik / Darb lines staff typed
 * as Other Charges - Salik goes sometimes to one, sometimes to the other, even on one contract.
 */
export function ejarTolls(ej) {
    const item = calcItems(ej).find((x) => Number(x.itemTypeId) === EJAR_CALC_ITEM.tolls) || null;
    const line = item ? r2(item.netPrice ?? item.totalPrice) : 0;
    const labelled = (ej?.otherCharges || []).filter((x) => /salik|toll|darb|سالك|درب/i.test(String(x.comment || '')));
    const otherLabelled = r2(labelled.reduce((a, x) => a + (Number(x.amount?.value) || 0) * (x.isTaxable ? VAT_MULTIPLIER : 1), 0));
    return { line, otherLabelled, onEjar: r2(line + otherLabelled) };
}

/** Traffic fines as Ejar holds them (the Traffic Violation line; no VAT on a fine). */
export function ejarFines(ej) {
    const item = calcItems(ej).find((x) => Number(x.itemTypeId) === EJAR_CALC_ITEM.fines) || null;
    return { onEjar: item ? r2(item.netPrice ?? item.totalPrice) : 0 };
}
